import apiClient, { handleApiError } from '../utils/apiClient';
import {
  Doctor,
  DoctorFormData,
  DoctorListResponse,
  DoctorDetailResponse,
  DoctorSearchParams
} from '../types/doctor';

/**
 * APIレスポンスのフィールドをフロントエンド用に変換
 */
const mapDoctorResponse = (doctor: Doctor): Doctor => {
  console.log('🔍 マッピング前のdoctorデータ:', doctor);

  // contractオブジェクトからmedical_spreadsheet_urlを取得
  const contractData = (doctor as any).contract;

  const mapped = {
    ...doctor,
    // APIレスポンスの電話番号フィールドをフロントエンド用にマッピング
    personal_phone: doctor.personal_tel_number || doctor.personal_phone || '',
    company_phone: doctor.company_tel_number || doctor.company_phone || '',
    // contractオブジェクト内のmedical_spreadsheet_urlをトップレベルにマッピング
    medical_spreadsheet_url: contractData?.medical_spreadsheet_url || doctor.medical_spreadsheet_url || null
  };

  console.log('✅ マッピング後のdoctorデータ:', mapped);
  console.log('📱 personal_tel_number:', doctor.personal_tel_number);
  console.log('📱 company_tel_number:', doctor.company_tel_number);
  console.log('📋 medical_spreadsheet_url:', mapped.medical_spreadsheet_url);

  return mapped;
};

/**
 * 医師API通信サービス
 * バックエンドAPIとの通信を管理
 */
export const doctorApiService = {
  /**
   * 医師一覧取得
   * @param params 検索・フィルタリングパラメータ
   * @returns 医師一覧データ
   */
  async getDoctors(params?: DoctorSearchParams): Promise<DoctorListResponse> {
    try {
      const response = await apiClient.get<DoctorListResponse>('/api/doctors', { params });
      // レスポンスデータの医師情報をマッピング
      const mappedData = response.data.data.map(mapDoctorResponse);
      return {
        ...response.data,
        data: mappedData
      };
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師詳細取得
   * @param id 医師ID
   * @returns 医師詳細データ
   */
  async getDoctor(id: number): Promise<DoctorDetailResponse> {
    try {
      const response = await apiClient.get<DoctorDetailResponse>(`/api/doctors/${id}`);
      console.log('🌐 APIレスポンス全体:', response.data);
      console.log('👨‍⚕️ APIレスポンスのdoctorデータ:', response.data.data);
      
      // レスポンスデータの医師情報をマッピング
      return {
        ...response.data,
        data: mapDoctorResponse(response.data.data)
      };
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師新規登録
   * @param data 医師登録データ
   * @returns 登録した医師データ
   */
  async createDoctor(data: DoctorFormData): Promise<DoctorDetailResponse> {
    try {
      // 日付フォーマットをyyyy/mm/dd形式に変換
      const formatDateForApi = (dateStr: string): string => {
        if (!dateStr) return dateStr;
        // yyyy-mm-dd形式をyyyy/mm/dd形式に変換
        return dateStr.replace(/-/g, '/');
      };
      
      // APIのフィールド名に合わせてデータを変換
      const requestData = {
        ...data,
        // 日付フォーマットの変換
        start_date: formatDateForApi(data.start_date),
        termination_date: data.termination_date ? formatDateForApi(data.termination_date) : null,
        // 電話番号フィールド名をAPIに合わせる
        personal_tel_number: data.personal_phone || '',
        company_tel_number: data.company_phone || '',
        // contact_emailsはそのまま
        contact_emails: data.contact_emails,
        // スプレッドシートURLはそのまま送信
        medical_spreadsheet_url: data.medical_spreadsheet_url || null,
        // supported_services と supported_languages はそのまま送信（文字列配列）
        supported_services: data.supported_services || [],
        supported_languages: data.supported_languages || []
      };

      // フロントエンド用のフィールドを削除
      delete (requestData as any).personal_phone;
      delete (requestData as any).company_phone;

      const response = await apiClient.post<DoctorDetailResponse>('/api/doctors', requestData);
      // レスポンスデータの医師情報をマッピング
      return {
        ...response.data,
        data: mapDoctorResponse(response.data.data)
      };
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師情報更新
   * @param id 医師ID
   * @param data 更新データ
   * @returns 更新した医師データ
   */
  async updateDoctor(id: number, data: DoctorFormData): Promise<DoctorDetailResponse> {
    try {
      // 日付フォーマットをyyyy/mm/dd形式に変換
      const formatDateForApi = (dateStr: string): string => {
        if (!dateStr) return dateStr;
        // yyyy-mm-dd形式をyyyy/mm/dd形式に変換
        return dateStr.replace(/-/g, '/');
      };
      
      // APIのフィールド名に合わせてデータを変換
      const requestData = {
        ...data,
        // 日付フォーマットの変換
        start_date: formatDateForApi(data.start_date),
        termination_date: data.termination_date ? formatDateForApi(data.termination_date) : null,
        // 電話番号フィールド名をAPIに合わせる
        personal_tel_number: data.personal_phone || '',
        company_tel_number: data.company_phone || '',
        // contact_emailsはそのまま
        contact_emails: data.contact_emails,
        // スプレッドシートURLはそのまま送信
        medical_spreadsheet_url: data.medical_spreadsheet_url || null,
        // supported_services と supported_languages はそのまま送信（文字列配列）
        supported_services: data.supported_services || [],
        supported_languages: data.supported_languages || []
      };

      // フロントエンド用のフィールドを削除
      delete (requestData as any).personal_phone;
      delete (requestData as any).company_phone;

      const response = await apiClient.put<DoctorDetailResponse>(`/api/doctors/${id}`, requestData);
      // レスポンスデータの医師情報をマッピング
      return {
        ...response.data,
        data: mapDoctorResponse(response.data.data)
      };
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師削除
   * @param id 医師ID
   * @returns 削除成功レスポンス
   */
  async deleteDoctor(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.delete<{ success: boolean; message: string }>(`/api/doctors/${id}`);
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師の稼働ステータス一括更新
   * @param ids 医師IDの配列
   * @param status 新しいステータス
   * @returns 更新結果
   */
  async updateDoctorsStatus(ids: number[], status: string): Promise<{ success: boolean; updated: number }> {
    try {
      const response = await apiClient.patch<{ success: boolean; updated: number }>('/api/doctors/batch-status', {
        doctor_ids: ids,
        status
      });
      return response.data;
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  }
};

// 実際のAPIサービスをエクスポート
export default doctorApiService;